/* eslint-disable react/require-default-props, react/no-unused-prop-types, @typescript-eslint/no-unused-vars */
import React from 'react';

import type { GradientConfig, SegmentData } from './types';

/**
 * Props for BarSegmentRenderer component
 */
export interface BarSegmentRendererProps {
  /** Segment data to render */
  segment: SegmentData;
  /** X position of the segment (left edge) */
  x: number;
  /** Y position of the segment (top edge) */
  y: number;
  /** Width of the segment in pixels */
  width: number;
  /** Height of the segment in pixels (may be animated) */
  height: number;
  /** Whether this is the topmost segment (determines if rounded corners apply) */
  isTopSegment: boolean;
  /** Border radius for top corners (only applied to top segment) */
  barRadius?: number;
  /** Whether this segment is currently hovered */
  isHovered?: boolean;
  /** Label/identifier for accessibility title */
  label?: string;
  /** Bar index for identifying which bar this segment belongs to - not used in component but kept for API compatibility */
  barIndex?: number;
  /** Callback when mouse enters the segment */
  onMouseEnter?: () => void;
  /** Callback when mouse leaves the segment */
  onMouseLeave?: () => void;
  /** Optional CSS class name */
  className?: string;
  /** Optional gradient configuration for this segment */
  gradient?: GradientConfig;
}
const BarSegmentRenderer = React.memo(
  ({
    segment,
    x,
    y,
    width,
    height,
    isTopSegment,
    isHovered = false,
    label,
    onMouseEnter,
    onMouseLeave,
    className,
    gradient,
  }: BarSegmentRendererProps) => {
    const renderedHeight = Math.max(height, 1);

    // Determine if we should use gradient (when gradient config is provided)
    const gradientId = `gradient-${segment.categoryId}-${x}-${y}`;
    const shouldUseGradient = !isTopSegment && gradient;
    const gradientOffset = gradient?.offset ?? 68.5;

    return (
      <g className={`BarSegmentRenderer ${className ?? ''}`}>
        {/* Define gradient if needed */}
        {shouldUseGradient && (
          <defs>
            <linearGradient id={gradientId} x1='0%' y1='0%' x2='0%' y2='100%'>
              <stop offset='0%' stopColor={gradient.startColor} />
              <stop
                offset={`${gradientOffset}%`}
                stopColor={gradient.endColor}
              />
            </linearGradient>
          </defs>
        )}

        <rect
          x={x}
          y={y}
          width={width}
          height={renderedHeight}
          fill={shouldUseGradient ? `url(#${gradientId})` : segment.color}
          rx={0}
          ry={0}
          shapeRendering='crispEdges'
          style={{
            filter: isHovered ? 'brightness(1.15)' : 'none',
            transition: 'filter 200ms ease',
            cursor: 'pointer',
          }}
          pointerEvents='auto'
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}>
          <title>
            {label
              ? `${segment.label}: ${segment.value} in ${label}`
              : `${segment.label}: ${segment.value}`}
          </title>
        </rect>
      </g>
    );
  }
);

BarSegmentRenderer.displayName = 'BarSegmentRenderer';

export default BarSegmentRenderer;
